import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { BRAND } from "@/lib/site";
import { ShieldCheck, FileCheck2, SearchCheck, Tags, FolderArchive, Leaf } from "lucide-react";

const items = [
  { icon: ShieldCheck, t: "Environment Agency Registration", d: `DES-CARTA LTD holds an Upper Tier Waste Broker Registration with the Environment Agency. Registration number: ${BRAND.eaReg}.` },
  { icon: FileCheck2, t: "Duty of Care", d: "Commercial waste movements require appropriate classification, documentation and authorised counterparties. Our processes are designed around applicable Duty of Care requirements." },
  { icon: SearchCheck, t: "Counterparty Verification", d: "Appropriate checks are undertaken on carriers and receiving facilities before movements are arranged." },
  { icon: Tags, t: "Waste Classification", d: "Waste descriptions and relevant waste classification codes form part of the transaction documentation." },
  { icon: FolderArchive, t: "Documentation", d: "Appropriate waste-transfer information and supporting transaction records are maintained in accordance with applicable requirements." },
];

export default function Compliance() {
  return (
    <>
      <section className="pt-36 pb-16 px-4 sm:px-6 lg:px-8 border-b border-dc-border dc-grid-lines">
        <div className="max-w-[1400px] mx-auto">
          <span className="dc-label">Compliance & Duty of Care</span>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="font-display uppercase tracking-tighter text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[0.92] mt-6 max-w-4xl">
            Compliance-Led Processes
          </motion.h1>
          <p className="mt-6 max-w-2xl text-dc-text2 leading-relaxed">DES-CARTA LTD operates within the applicable UK waste-management regulatory framework. Commercial waste is handled through documented, counterparty-verified processes designed around applicable Duty of Care requirements.</p>
        </div>
      </section>

      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-1">
            <div className="border border-dc-border bg-dc-surface p-8 sticky top-28">
              <div className="dc-label mb-4">Registration</div>
              <div className="text-sm text-dc-text2 mb-1">Upper Tier Waste Broker</div>
              <div className="font-mono text-dc-accent text-lg">{BRAND.eaReg}</div>
              <div className="text-xs text-dc-muted mt-1 mb-6">Environment Agency</div>
              <div className="dc-label mb-2">Registered Office</div>
              <div className="text-sm text-dc-text2">{BRAND.location}</div>
              <div className="dc-label mt-6 mb-2">Contact</div>
              <a href={`mailto:${BRAND.email}`} className="text-sm text-white hover:text-dc-accent transition-colors font-mono break-all">{BRAND.email}</a>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="border-t border-l border-dc-border">
              {items.map((it, i) => (
                <Reveal key={it.t} delay={i * 0.05}>
                  <div className="border-r border-b border-dc-border p-8 flex gap-5">
                    <it.icon className="text-dc-accent shrink-0 mt-1" size={26} strokeWidth={1.5} />
                    <div>
                      <h3 className="font-display text-xl mb-2">{it.t}</h3>
                      <p className="text-dc-text2 text-sm leading-relaxed">{it.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-dc-surface/40 border-y border-dc-border">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <span className="dc-label">Sustainability</span>
            <h2 className="font-display uppercase tracking-tight text-4xl lg:text-5xl font-semibold leading-[0.95] mt-4 flex items-start gap-4">
              <Leaf className="text-dc-accent mt-2 shrink-0" size={32} strokeWidth={1.5} /> Keep Recoverable Materials in Productive Use
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-dc-text2 leading-relaxed mb-6">Our contribution to sustainability is practical rather than promotional. By supporting material segregation, recycling and resource efficiency, we help keep recoverable fibre in productive use through transparent waste flows and responsible supply-chain management.</p>
            <div className="grid grid-cols-2 gap-3">
              {["Material segregation", "Recycling", "Resource efficiency", "Transparent waste flows", "Responsible supply chain", "Recoverable fibre in use"].map((t) => (
                <div key={t} className="border border-dc-border p-3 text-sm text-white">{t}</div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
